import FeatherIcon from 'feather-icons-react';
import { useState } from 'react';
import { Container, Nav, Navbar } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link, matchPath, useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';

import { getInitials } from '../helpers/getInitials';
import { queryCache } from '../index';
import { role } from '../permissions';
import { logoutUser } from '../store/redux/userRedux';
import { User } from '../types';
import { COMPANY_NAME, TENANT_KEY, TOKEN_KEY } from '../utils/tenant';

interface RootState {
  user: {
    currentUser: User | null;
  };
}

const rolePermissions = {
  reports: role.generalGroup,
  users: role.adminGroup,
  customers: role.generalGroup,
  outlets: role.generalGroup,
  drivers: role.supervisorGroup,
  orders: role.generalGroup,
  returnOrders: role.generalGroup,
  invoices: role.generalGroup,
  products: role.stockGroup,
  editProducts: role.supervisorGroup,
  stockAdjustment: role.stockGroup,
  empties: role.stockGroup,
  financialTransactions: role.financeGroup,
  credits: role.generalGroup,
  commissions: role.financeGroup
};

export const useHasAccess = () => {
  const userDetails = useSelector((state: RootState) => state.user.currentUser);
  const userRole = userDetails?.role;

  return (section: keyof typeof rolePermissions): boolean => {
    if (!userRole) return false;
    return rolePermissions[section]?.includes(userRole) ?? false;
  };
};

const Sidebar = ({ ...props }) => {
  const hasAccess = useHasAccess();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toggler = <Navbar.Toggle />;

  const userDetails = useSelector((state: RootState) => state.user.currentUser);
  const userRole = userDetails?.role;
  const settingsLink = userRole === 'admin' ? '/settings/admin' : '/settings/user';
  const initials = getInitials(userDetails?.fullName);

  const brand = (
    <Navbar.Brand as={Link} to="/">
      <img className="navbar-brand-img" src={`/tenants/${TENANT_KEY}/logo.svg`} alt={COMPANY_NAME} />
    </Navbar.Brand>
  );

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    dispatch(logoutUser());
    queryCache.clear();
    navigate('/login');
  };

  const [collapseEmpties, setCollapseEmpties] = useState(false);

  const toggleEmpties = () => {
    setCollapseEmpties(prevState => !prevState);
  };
  const getIcon = (isOpen: boolean) => (isOpen ? 'chevron-up' : 'chevron-down');

  const collapse = (
    <Navbar.Collapse {...props}>
      <Nav>
        {/* Analytics */}
        {hasAccess('reports') && (
          <>
            {' '}
            <Nav.Item className="navbar-heading mx-4 my-3">Analytics</Nav.Item>
            {/* <Nav.Item>
          <Nav.Link as={Link} to="/dashboard" active={location.pathname === '/dashboard'}>
            <FeatherIcon icon="pie-chart" size="17" />
            Dashboard
          </Nav.Link>
        </Nav.Item> */}
            <Nav.Item>
              <Nav.Link as={Link} to="/reports" active={location.pathname === '/reports'}>
                <FeatherIcon icon="file-text" size="17" />
                Reports
              </Nav.Link>
            </Nav.Item>
          </>
        )}
        {/* Admin */}
        {(hasAccess('users') || hasAccess('customers') || hasAccess('outlets') || hasAccess('drivers')) && (
          <>
            <Nav.Item className="navbar-heading mx-4 my-3">Admin</Nav.Item>
            {hasAccess('users') && (
              <Nav.Item>
                <Nav.Link as={Link} to="/users/active" active={!!matchPath('/users/*', location.pathname)}>
                  <FeatherIcon icon="user" size="17" />
                  Users
                </Nav.Link>
              </Nav.Item>
            )}
            {hasAccess('customers') && (
              <Nav.Item>
                <Nav.Link as={Link} to="/customers/active" active={!!matchPath('/customers/*', location.pathname)}>
                  <FeatherIcon icon="users" size="17" />
                  Customers
                </Nav.Link>
              </Nav.Item>
            )}
            {hasAccess('outlets') && (
              <Nav.Item>
                <Nav.Link as={Link} to="/outlets/active" active={!!matchPath('/outlets/*', location.pathname)}>
                  <FeatherIcon icon="map" size="17" />
                  Outlets
                </Nav.Link>
              </Nav.Item>
            )}
            {hasAccess('drivers') && (
              <Nav.Item>
                <Nav.Link as={Link} to="/drivers/active" active={!!matchPath('/drivers/*', location.pathname)}>
                  <FeatherIcon icon="truck" size="17" />
                  Drivers
                </Nav.Link>
              </Nav.Item>
            )}
          </>
        )}

        {/* Sales */}
        {(hasAccess('orders') || hasAccess('returnOrders') || hasAccess('invoices')) && (
          <>
            <Nav.Item className="navbar-heading mx-4 my-3">Sales</Nav.Item>
            {hasAccess('orders') && (
              <Nav.Item>
                <Nav.Link as={Link} to="/orders/all" active={!!matchPath('/orders/*', location.pathname)}>
                  <FeatherIcon icon="shopping-bag" size="17" />
                  Orders
                </Nav.Link>
              </Nav.Item>
            )}
            {hasAccess('returnOrders') && (
              <Nav.Item>
                <Nav.Link as={Link} to="/return-orders/all" active={!!matchPath('/return-orders/*', location.pathname)}>
                  <FeatherIcon icon="shopping-bag" size="17" />
                  Return Orders
                </Nav.Link>
              </Nav.Item>
            )}
            {hasAccess('invoices') && (
              <Nav.Item>
                <Nav.Link as={Link} to="/invoices/all" active={!!matchPath('/invoices/*', location.pathname)}>
                  <FeatherIcon icon="file-text" size="17" />
                  Invoices
                </Nav.Link>
              </Nav.Item>
            )}
          </>
        )}
        {/* Inventory */}
        {(hasAccess('products') || hasAccess('stockAdjustment') || hasAccess('empties')) && (
          <>
            <Nav.Item className="navbar-heading mx-4 my-3">Inventory</Nav.Item>
            {hasAccess('products') && (
              <Nav.Item>
                <Nav.Link as={Link} to="/products/active" active={!!matchPath('/products/*', location.pathname)}>
                  <FeatherIcon icon="package" size="17" />
                  Products
                </Nav.Link>
              </Nav.Item>
            )}
            {hasAccess('stockAdjustment') && (
              <Nav.Item>
                <Nav.Link
                  as={Link}
                  to="/stock-adjustment/all"
                  active={!!matchPath('/stock-adjustment/*', location.pathname)}
                >
                  <FeatherIcon icon="box" size="17" />
                  Stock Adjustment
                </Nav.Link>
              </Nav.Item>
            )}

            {/* Collapsible Empties Section */}
            {hasAccess('empties') && (
              <Nav.Item>
                <Nav.Item onClick={toggleEmpties}>
                  <Nav.Link active={location.pathname === '/empties'}>
                    <>
                      <FeatherIcon icon="box" size="17" />
                      <div className="d-flex justify-content-between w-100">
                        <span>Empties</span>
                        <FeatherIcon icon={getIcon(collapseEmpties)} size="1em" />
                      </div>
                    </>
                  </Nav.Link>
                </Nav.Item>
                {collapseEmpties && (
                  <div className="nav nav-sm flex-column">
                    <Nav.Item>
                      <Nav.Link as={Link} to="/empties" active={location.pathname === '/empties'}>
                        {/* <FeatherIcon icon="box" size="17" /> */}
                        Empties Counter
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link as={Link} to="/empties-history" active={location.pathname === '/empties-history'}>
                        {/* <FeatherIcon icon="box" size="17" /> */}
                        Empties History
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link as={Link} to="/return-empties" active={location.pathname === '/return-empties'}>
                        {/* <FeatherIcon icon="box" size="17" /> */}
                        Return Empties
                      </Nav.Link>
                    </Nav.Item>
                  </div>
                )}
              </Nav.Item>
            )}
          </>
        )}
        {/* <Nav.Item>
          <Nav.Link as={Link} to="/sku" active={location.pathname === '/sku'}>
            <FeatherIcon icon="tag" size="17" />
            SKU
          </Nav.Link>
        </Nav.Item> */}

        {/* Finance */}
        {(hasAccess('financialTransactions') || hasAccess('credits') || hasAccess('commissions')) && (
          <>
            <Nav.Item className="navbar-heading mx-4 my-3">Finance</Nav.Item>
            {hasAccess('financialTransactions') && (
              <Nav.Item>
                <Nav.Link
                  as={Link}
                  to="/financial-transaction/all"
                  active={!!matchPath('/financial-transaction/*', location.pathname)}
                >
                  <FeatherIcon icon="shopping-bag" size="17" />
                  Financial Transactions
                </Nav.Link>
              </Nav.Item>
            )}
            {hasAccess('credits') && (
              <Nav.Item>
                <Nav.Link as={Link} to="/credits/all" active={!!matchPath('/credits/*', location.pathname)}>
                  <FeatherIcon icon="shopping-bag" size="17" />
                  Credits
                </Nav.Link>
              </Nav.Item>
            )}
            {hasAccess('commissions') && (
              <Nav.Item>
                <Nav.Link as={Link} to="/commissions" active={location.pathname === '/commissions'}>
                  <FeatherIcon icon="box" size="17" />
                  Commissions
                </Nav.Link>
              </Nav.Item>
            )}
          </>
        )}
      </Nav>
    </Navbar.Collapse>
  );

  const user = (
    <div className="navbar-user d-flex w-100 px-2 mt-4">
      <Link
        to={settingsLink}
        className={`navbar-user-link ${matchPath('/settings/*', location.pathname) ? 'active' : ''}`}
        title="Settings"
      >
        <FeatherIcon icon="settings" size="17" />
      </Link>
      <div className="avatar avatar-sm avatar-online">
        <span className="avatar-title rounded-circle bg-secondary">{initials}</span>
      </div>
      <Link className="navbar-user-link" to="/login" title="Log out" onClick={logout}>
        <FeatherIcon icon="log-out" size="17" />
      </Link>
    </div>
  );

  return (
    <Navbar expand="md" className="navbar-vertical fixed-start" {...props}>
      <Container fluid>
        {toggler}
        {brand}
        {collapse}
        {user}
      </Container>
    </Navbar>
  );
};

export default Sidebar;
